<script setup lang="ts">
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import { Button } from '@/components/ui/button'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
  import { CheckIcon, X } from 'lucide-vue-next'
  import { computed } from 'vue'
  import type { CustomerEntity, ProposedEntityChange } from '@/types/generated'
  import { useTranslation } from '@/composables/useTranslation'

  // Inline-resolve mode (e.g. email send pre-flight) shows pending client
  // first_name / last_name / email proposals with accept/reject controls inside
  // the dialog itself. Legacy mode (export flow) keeps the original
  // description + Cancel + Review-in-entities-page footer. Both modes are
  // selected by whether the parent passes `pendingChanges`.
  interface Props {
    open: boolean
    source?: 'email' | 'export'
    caseUuid?: string
    pendingChanges?: ProposedEntityChange[]
    clientEntity?: CustomerEntity | null
    processingChangeUuid?: string | null
  }

  interface Emits {
    (e: 'update:open', value: boolean): void
    (e: 'cancel'): void
    (e: 'review'): void
    (e: 'accept', changeUuid: string): void
    (e: 'reject', changeUuid: string): void
  }

  const props = withDefaults(defineProps<Props>(), {
    source: 'export',
    pendingChanges: undefined,
    clientEntity: null,
    processingChangeUuid: null
  })
  const emit = defineEmits<Emits>()

  const { t } = useTranslation({
    en: {
      title: 'Action Required',
      legacyDescription:
        'New proposals for important participant details have not yet been confirmed. Please review them before proceeding.',
      inlineDescription:
        'Please review the proposed updates to the client\u2019s details before sending.',
      cancel: 'Cancel',
      review: 'Review Participant Details',
      accept: 'Accept',
      reject: 'Reject',
      first_name: 'First name',
      last_name: 'Last name',
      email: 'Email',
      emptyValue: '\u2014'
    },
    de: {
      title: 'Erforderliche Maßnahme',
      legacyDescription:
        'Neue Vorschläge für wichtige Beteiligtedetails wurden noch nicht bestätigt. Bitte überprüfen Sie diese, bevor Sie fortfahren.',
      inlineDescription:
        'Bitte prüfen Sie die folgenden Vorschläge zu den Mandantendaten, bevor Sie senden.',
      cancel: 'Abbrechen',
      review: 'Beteiligtedetails überprüfen',
      accept: 'Bestätigen',
      reject: 'Ablehnen',
      first_name: 'Vorname',
      last_name: 'Nachname',
      email: 'E-Mail',
      emptyValue: '\u2014'
    }
  })

  // Field gating must mirror the parent's send-blocking predicate. If the
  // parent passes through changes on other fields they are silently filtered
  // out — the dialog only ever surfaces these three.
  const FIELD_KEYS = ['first_name', 'last_name', 'email'] as const
  type FieldKey = (typeof FIELD_KEYS)[number]
  const isFieldKey = (s: string | null | undefined): s is FieldKey =>
    !!s && (FIELD_KEYS as readonly string[]).includes(s)

  // Both `current_value` and `proposed_value` are JSON-serialized as
  // `{ value: actualValue }` server-side; unwrap before display.
  const unwrapJsonValue = (raw: unknown): string => {
    if (raw && typeof raw === 'object' && 'value' in (raw as object)) {
      const inner = (raw as { value: unknown }).value
      if (inner == null || inner === '') return t('emptyValue')
      return String(inner)
    }
    if (raw == null || raw === '') return t('emptyValue')
    return String(raw)
  }

  const currentValueForField = (fieldName: FieldKey): string => {
    const entity = props.clientEntity as Record<string, unknown> | null
    if (!entity) return t('emptyValue')
    const raw = entity[fieldName]
    if (raw == null || raw === '') return t('emptyValue')
    return String(raw)
  }

  const isInlineMode = computed(() => Array.isArray(props.pendingChanges))

  const visibleChanges = computed<ProposedEntityChange[]>(() =>
    (props.pendingChanges ?? []).filter((change) => isFieldKey(change.field_name))
  )

  const handleCancel = () => {
    emit('cancel')
    emit('update:open', false)
  }

  const handleReview = () => {
    emit('review')
    emit('update:open', false)
  }

  const handleAccept = (uuid: string | undefined) => {
    if (uuid) emit('accept', uuid)
  }

  const handleReject = (uuid: string | undefined) => {
    if (uuid) emit('reject', uuid)
  }
</script>

<template>
  <Dialog
    :open="open"
    @update:open="(value) => emit('update:open', value)"
  >
    <DialogContent class="gap-6">
      <DialogHeader class="gap-2">
        <DialogTitle>{{ t('title') }}</DialogTitle>
        <DialogDescription>
          {{ isInlineMode ? t('inlineDescription') : t('legacyDescription') }}
        </DialogDescription>
      </DialogHeader>

      <div
        v-if="isInlineMode"
        class="flex flex-col gap-3"
      >
        <div
          v-for="change in visibleChanges"
          :key="change.uuid"
          class="flex flex-col gap-1.5"
        >
          <span class="text-sm font-medium text-gray-700">
            {{ t(change.field_name as string) }}
          </span>
          <div
            class="flex w-full items-center gap-1.5 rounded-lg border border-dashed border-purple-600 bg-purple-50
              px-2.5 py-2 text-sm"
          >
            <div class="flex-1 cursor-default truncate">
              <div class="truncate text-gray-500 line-through">
                {{ currentValueForField(change.field_name as FieldKey) }}
              </div>
              <div class="truncate text-gray-800">
                {{ unwrapJsonValue(change.proposed_value) }}
              </div>
            </div>
            <div class="flex items-center gap-1">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger as-child>
                    <Button
                      variant="secondary"
                      size="icon"
                      class="h-6 w-6 border border-purple-200 text-purple-600 hover:border-purple-300 hover:bg-gray-100
                        hover:text-purple-600"
                      :disabled="processingChangeUuid === change.uuid"
                      @click="handleReject(change.uuid)"
                    >
                      <X class="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    variant="secondary"
                    show-arrow
                  >
                    <p>{{ t('reject') }}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger as-child>
                    <Button
                      variant="secondary"
                      size="icon"
                      class="h-6 w-6 border border-purple-200 text-purple-600 hover:border-purple-300 hover:bg-gray-100
                        hover:text-purple-600"
                      :disabled="processingChangeUuid === change.uuid"
                      @click="handleAccept(change.uuid)"
                    >
                      <CheckIcon class="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    variant="secondary"
                    show-arrow
                  >
                    <p>{{ t('accept') }}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button
          class="flex-1"
          variant="secondary"
          @click="handleCancel"
        >
          {{ t('cancel') }}
        </Button>
        <Button
          v-if="!isInlineMode"
          class="flex-1"
          @click="handleReview"
        >
          {{ t('review') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
